import { ThemeConfig, theme } from 'antd';
import { useEffect, useMemo, useState } from 'react';

function getCssVars() {
  return {
    colorActionBgNeutralBase: getCssVariable('--cont-color-action-bg-neutral-base'),
    colorActionBgNeutralModerate: getCssVariable('--cont-color-action-bg-neutral-moderate'),
    colorActionBgPrimaryBase: getCssVariable('--cont-color-action-bg-primary-base'),
    colorActionBgPrimaryModerate: getCssVariable('--cont-color-action-bg-primary-moderate'),
    colorActionBorderNeutralBase: getCssVariable('--cont-color-action-border-neutral-base'),
    colorActionBorderNeutralBold: getCssVariable('--cont-color-action-border-neutral-bold'),
    colorActionBorderNeutralModerate: getCssVariable('--cont-color-action-border-neutral-moderate'),
    colorActionFgOnNegativeMinimal: getCssVariable('--cont-color-action-fg-on-negative-minimal'),
    colorActionFgOnNeutralBold: getCssVariable('--cont-color-action-fg-on-neutral-bold'),
    colorActionFgOnNeutralModerate: getCssVariable('--cont-color-action-fg-on-neutral-moderate'),
    colorActionFgOnPrimaryBase: getCssVariable('--cont-color-action-fg-on-primary-base'),
    colorCommonContainerSurfaceLayer1: getCssVariable('--cont-color-common-container-surface-layer-1'),
    colorCommonContainerSurfaceLayer2: getCssVariable('--cont-color-common-container-surface-layer-2'),
    colorCommonContainerSurfaceLayer3: getCssVariable('--cont-color-common-container-surface-layer-3'),
    colorCommonContainerSurfaceLayer4: getCssVariable('--cont-color-common-container-surface-layer-4'),
    colorCommonContainerSurfaceOnCanvasSubtle: getCssVariable('--cont-color-common-container-surface-on-canvas-subtle'),
    colorCommonContainerSurfaceOnLayer1Subtle: getCssVariable('--cont-color-common-container-surface-on-layer-1-subtle'),
    colorCommonFgGenericBold: getCssVariable('--cont-color-common-fg-generic-bold'),
    colorCommonFgGenericDisabledOnDisabledBold: getCssVariable('--cont-color-common-fg-generic-disabled-on-disabled-bold'),
    colorCommonFgGenericModerate: getCssVariable('--cont-color-common-fg-generic-moderate'),
    colorCommonFgGenericSubtle: getCssVariable('--cont-color-common-fg-generic-subtle'),
    colorControlBgBase: getCssVariable('--cont-color-control-bg-base'),
    colorControlBgBold: getCssVariable('--cont-color-control-bg-bold'),
    colorControlBgModerate: getCssVariable('--cont-color-control-bg-moderate'),
    colorControlFgOnBold: getCssVariable('--cont-color-control-fg-on-bold'),
    colorFieldBgOnLayer1Moderate: getCssVariable('--cont-color-field-bg-on-layer-1-moderate'),
    colorFieldBgOnLayer1Subtle: getCssVariable('--cont-color-field-bg-on-layer-1-subtle'),
    colorFieldBorderOnLayer1Moderate: getCssVariable('--cont-color-field-border-on-layer-1-moderate'),
    colorFieldBorderOnLayer1Subtle: getCssVariable('--cont-color-field-border-on-layer-1-subtle'),
    colorFieldPlaceholder: getCssVariable('--color-field-placeholder'),
    colorMenuFgOnMinimal: getCssVariable('--cont-color-menu-fg-on-minimal'),
    colorStatusBgInfoMinimal: getCssVariable('--cont-color-status-bg-info-minimal'),
    colorStatusBgNegativeMinimal: getCssVariable('--cont-color-status-bg-negative-minimal'),
    colorStatusBgPositiveMinimal: getCssVariable('--cont-color-status-bg-positive-minimal'),
    colorStatusBgWarningMinimal: getCssVariable('--cont-color-status-bg-warning-minimal'),
    colorStatusBorderInfoBase: getCssVariable('--cont-color-status-border-info-base'),
    colorStatusBorderNegativeBase: getCssVariable('--cont-color-status-border-negative-base'),
    colorStatusBorderPositiveBase: getCssVariable('--cont-color-status-border-positive-base'),
    colorStatusBorderWarningBase: getCssVariable('--cont-color-status-border-warning-base'),
    colorStatusFgOnInfoMinimal: getCssVariable('--cont-color-status-fg-on-info-minimal'),
    colorStatusFgOnNegativeMinimal: getCssVariable('--cont-color-status-fg-on-negative-minimal'),
    colorStatusFgOnPositiveMinimal: getCssVariable('--cont-color-status-fg-on-positive-minimal'),
    colorStatusFgOnWarningMinimal: getCssVariable('--cont-color-status-fg-on-warning-minimal'),
    radiusAction: getCssVariableAsNumber('--cont-radius-action'),
    radiusCommonMd: getCssVariableAsNumber('--cont-radius-common-md'),
    radiusCommonSm: getCssVariableAsNumber('--cont-radius-common-sm'),
    radiusControl: getCssVariableAsNumber('--cont-radius-control'),
    typographyFieldMd: getCssVariable('--cont-typography-field-md'),
    typographyFontFamily100: getCssVariable('--core-typography-font-family-100'),
  };
}

export function useAntDTheme(selectedTheme: 'light' | 'dark'): ThemeConfig {
  const [prefersColorScheme, setPrefersColorScheme] = useState<string | null>(null);

  useEffect(() => {
    const observer = new MutationObserver(() => setPrefersColorScheme(getPrefersColorScheme()));

    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-prefers-color-scheme'] });

    setPrefersColorScheme(getPrefersColorScheme());

    return () => observer.disconnect();
  }, []);

  return useMemo(() => {
    const cssVars = getCssVars();

    const config: ThemeConfig = {
      algorithm: (token) => (selectedTheme === 'light' ? theme.defaultAlgorithm(token) : theme.darkAlgorithm(token)),
      token: {
        borderRadius: cssVars.radiusCommonSm,
        borderRadiusLG: cssVars.radiusCommonMd,
        borderRadiusSM: cssVars.radiusControl,
        colorBgBase: cssVars.colorActionBgNeutralBase,
        colorBgContainer: cssVars.colorActionBgNeutralBase,
        colorBgContainerDisabled: cssVars.colorFieldBgOnLayer1Moderate,
        colorBgElevated: cssVars.colorCommonContainerSurfaceLayer4,
        colorBgLayout: 'transparent',
        colorBorder: cssVars.colorActionBorderNeutralBase,
        colorBorderSecondary: cssVars.colorCommonContainerSurfaceOnCanvasSubtle,
        colorError: cssVars.colorStatusFgOnNegativeMinimal,
        colorErrorBg: cssVars.colorStatusBgNegativeMinimal,
        colorErrorBorder: cssVars.colorStatusBorderNegativeBase,
        colorErrorBorderHover: cssVars.colorActionFgOnNegativeMinimal,
        colorErrorHover: cssVars.colorActionFgOnNegativeMinimal,
        colorInfo: cssVars.colorStatusFgOnInfoMinimal,
        colorInfoBg: cssVars.colorStatusBgInfoMinimal,
        colorInfoBorder: cssVars.colorStatusBorderInfoBase,
        colorLink: cssVars.colorCommonFgGenericBold,
        colorPrimary: cssVars.colorActionBgPrimaryBase,
        colorPrimaryHover: cssVars.colorActionBgPrimaryModerate,
        colorSplit: cssVars.colorCommonContainerSurfaceOnCanvasSubtle,
        colorSuccess: cssVars.colorStatusFgOnPositiveMinimal,
        colorSuccessBg: cssVars.colorStatusBgPositiveMinimal,
        colorSuccessBorder: cssVars.colorStatusBorderPositiveBase,
        colorTextDescription: cssVars.colorCommonFgGenericSubtle,
        colorTextDisabled: '#6B6B6B', // cssVars.colorCommonFgGenericDisabledOnDisabledBold fails a11y tests
        colorTextPlaceholder: cssVars.colorFieldPlaceholder,
        colorWarning: cssVars.colorStatusFgOnWarningMinimal,
        colorWarningBg: cssVars.colorStatusBgWarningMinimal,
        colorWarningBorder: cssVars.colorStatusBorderWarningBase,
        fontFamily: cssVars.typographyFontFamily100,
      },
      components: {
        Button: {
          defaultActiveBg: 'transparent',
          defaultActiveBorderColor: cssVars.colorActionBorderNeutralBold,
          defaultActiveColor: cssVars.colorActionFgOnNeutralBold,
          defaultHoverBg: cssVars.colorActionBgNeutralModerate,
          defaultHoverBorderColor: cssVars.colorActionBorderNeutralModerate,
          defaultHoverColor: cssVars.colorActionFgOnNeutralModerate,
          primaryShadow: 'none',
        },
        Card: {
          colorBgContainer: 'transparent',
        },
        Collapse: {
          contentBg: cssVars.colorCommonContainerSurfaceLayer1,
        },
        DatePicker: {
          activeBg: cssVars.colorFieldBgOnLayer1Subtle,
          activeBorderColor: cssVars.colorFieldBorderOnLayer1Moderate,
          activeShadow: 'none',
          borderRadius: 0,
          colorBgContainer: cssVars.colorFieldBgOnLayer1Subtle,
          colorBorder: cssVars.colorFieldBorderOnLayer1Subtle,
          colorPrimaryHover: cssVars.colorFieldBgOnLayer1Moderate,
          hoverBg: cssVars.colorFieldBgOnLayer1Moderate,
          lineWidth: 1.5,
        },
        Dropdown: {
          colorText: cssVars.colorMenuFgOnMinimal,
          paddingBlock: 1,
        },
        Input: {
          activeBg: cssVars.colorFieldBgOnLayer1Subtle,
          activeBorderColor: cssVars.colorFieldBorderOnLayer1Moderate,
          activeShadow: 'none',
          borderRadius: 0,
          colorBgContainer: cssVars.colorFieldBgOnLayer1Subtle,
          colorBorder: cssVars.colorFieldBorderOnLayer1Subtle,
          colorPrimaryHover: cssVars.colorFieldBgOnLayer1Moderate,
          hoverBg: cssVars.colorFieldBgOnLayer1Moderate,
          lineWidth: 1.5,
        },
        InputNumber: {
          activeBg: cssVars.colorFieldBgOnLayer1Subtle,
          activeBorderColor: cssVars.colorFieldBorderOnLayer1Moderate,
          activeShadow: 'none',
          borderRadius: 0,
          colorBgContainer: cssVars.colorFieldBgOnLayer1Subtle,
          colorBorder: cssVars.colorFieldBorderOnLayer1Subtle,
          colorPrimaryHover: cssVars.colorFieldBgOnLayer1Moderate,
          hoverBg: cssVars.colorFieldBgOnLayer1Moderate,
          lineWidth: 1.5,
        },
        Layout: {
          siderBg: 'transparent',
        },
        Menu: {
          groupTitleColor: cssVars.colorCommonFgGenericSubtle,
          itemBg: 'transparent',
          itemHeight: 32,
          itemSelectedBg: cssVars.colorActionBgPrimaryBase,
          itemSelectedColor: cssVars.colorActionFgOnPrimaryBase,
          subMenuItemBg: 'transparent',
        },
        Modal: {
          colorBgElevated: cssVars.colorCommonContainerSurfaceLayer1,
          paddingContentHorizontalLG: 0,
          paddingMD: 0,
        },
        Segmented: {
          trackBg: cssVars.colorFieldBgOnLayer1Subtle,
        },
        Select: {
          activeBorderColor: cssVars.colorFieldBorderOnLayer1Moderate,
          borderRadius: 0,
          colorBgContainer: cssVars.colorFieldBgOnLayer1Subtle,
          colorBorder: cssVars.colorFieldBorderOnLayer1Subtle,
          colorPrimaryHover: cssVars.colorFieldBgOnLayer1Moderate,
          controlOutlineWidth: 0,
          lineWidth: 1.5,
        },
        Switch: {
          fontSize: 12,
          handleSize: 12,
          innerMinMargin: 12,
          trackHeight: 20,
          trackMinWidth: 40,
          trackPadding: 4,
        },
        Table: {
          cellPaddingBlock: 12,
          cellPaddingBlockMD: 8,
          cellPaddingBlockSM: 4,
          colorBgContainer: cssVars.colorCommonContainerSurfaceLayer1,
          filterDropdownBg: cssVars.colorCommonContainerSurfaceLayer4,
        },
        Tabs: {
          colorBgContainer: cssVars.colorCommonContainerSurfaceLayer1,
          horizontalItemGutter: 0,
          horizontalItemPadding: '8px 12px',
          horizontalItemPaddingLG: '16px 16px',
          horizontalItemPaddingSM: '8px 8px',
          inkBarColor: cssVars.colorControlBgBase,
          itemActiveColor: cssVars.colorControlBgBase,
          itemHoverColor: 'unset',
          itemSelectedColor: 'unset',
        },
        Tag: {
          borderRadiusSM: cssVars.radiusCommonSm,
        },
        Tooltip: {
          colorBgSpotlight: cssVars.colorCommonContainerSurfaceLayer4,
          colorTextLightSolid: cssVars.colorCommonFgGenericModerate,
        },
        Radio: {
          borderRadius: cssVars.radiusAction,
          borderRadiusLG: cssVars.radiusAction,
          borderRadiusSM: cssVars.radiusAction,
          buttonBg: cssVars.colorCommonContainerSurfaceOnLayer1Subtle,
          buttonSolidCheckedActiveBg: cssVars.colorControlBgBold,
          buttonSolidCheckedBg: cssVars.colorControlBgBase,
          buttonSolidCheckedColor: cssVars.colorControlFgOnBold,
          buttonSolidCheckedHoverBg: cssVars.colorControlBgModerate,
          colorBorder: cssVars.colorCommonContainerSurfaceOnLayer1Subtle,
          dotSize: 10,
          fontSizeLG: 14,
          radioSize: 22,
        },
      },
    };
    return config;
  }, [selectedTheme, prefersColorScheme]);
}

function getPrefersColorScheme() {
  return document.documentElement.getAttribute('data-prefers-color-scheme');
}

function getCssVariable(variable: string) {
  return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
}

function getCssVariableAsNumber(variable: string) {
  return parseFloat(getCssVariable(variable));
}
